import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Sparkles, Send, Eye, Download, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export const NewsletterManager = ({ subsCount = 0 }) => {
  const [status, setStatus] = useState(null);
  const [subject, setSubject] = useState("");
  const [intro, setIntro] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const autoLoaded = useRef(false);

  const loadStatus = () => api.get("/admin/newsletter/status").then((r) => {
    setStatus(r.data);
    if (!autoLoaded.current && r.data?.due && r.data?.new_count > 0) {
      autoLoaded.current = true;
      generate();
    }
  }).catch(() => {});
  useEffect(() => { loadStatus(); }, [subsCount]);
  useEffect(() => {
    api.get("/auth/me").then((r) => { if (r.data?.email) setTestEmail(r.data.email); }).catch(() => {});
  }, []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/newsletter/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "iscritti-newsletter.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setExporting(false);
    }
  };

  const preview = async () => {
    const item_ids = items.filter((i) => selected[i.id]).map((i) => i.id);
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/newsletter/preview", { subject, intro, item_ids });
      setPreviewHtml(data.html);
      setPreviewOpen(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPreviewing(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.get("/admin/newsletter/suggest");
      setSubject(data.subject);
      setIntro(data.intro);
      setItems(data.items);
      setSelected(Object.fromEntries(data.items.map((i) => [i.id, true])));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const send = async () => {
    const item_ids = items.filter((i) => selected[i.id]).map((i) => i.id);
    setSending(true);
    try {
      const { data } = await api.post("/admin/newsletter/send", { subject, intro, item_ids });
      toast.success(`Newsletter inviata: ${data.sent} inviate, ${data.failed} non riuscite (${data.recipients} iscritti).`);
      setSubject(""); setIntro(""); setItems([]); setSelected({});
      loadStatus();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const sendTest = async () => {
    const item_ids = items.filter((i) => selected[i.id]).map((i) => i.id);
    setTesting(true);
    try {
      const { data } = await api.post("/admin/newsletter/send-test", { subject, intro, item_ids, email: testEmail });
      toast.success(`Email di prova inviata a ${data.email}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const nSelected = items.filter((i) => selected[i.id]).length;

  return (
    <div className="border border-border rounded-sm p-5 mb-6" data-testid="newsletter-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <h3 className="font-serif text-lg font-bold">Selezione settimanale</h3>
          {status && (
            <Badge variant={status.due ? "default" : "secondary"} className="rounded-sm text-[10px]" data-testid="newsletter-due-badge">
              {status.due
                ? (status.new_count > 0 ? `${status.new_count} nuovi · pronta da inviare` : "È ora di inviare")
                : `Inviata ${status.days_since ?? 0} g fa`}
            </Badge>
          )}
        </div>
        {status && (
          <div className="flex items-center gap-3">
            <p className="text-xs text-muted-foreground" data-testid="newsletter-confirmed-count">
              {status.confirmed} iscritti confermati · {status.pending} in attesa
            </p>
            <Button onClick={exportCsv} disabled={exporting} size="sm" variant="outline" className="rounded-sm gap-1.5" data-testid="newsletter-export-btn">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Esporta CSV
            </Button>
          </div>
        )}
      </div>

      <Button onClick={generate} disabled={generating} variant="outline" className="rounded-sm gap-1.5 mb-2" data-testid="newsletter-generate-btn">
        {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        Genera la selezione settimanale
      </Button>
      <p className="text-xs text-muted-foreground mb-4" data-testid="newsletter-semiauto-hint">
        Invio semi-automatico: ogni settimana, se ci sono nuovi contenuti, ricevi un promemoria e trovi qui la
        selezione già pronta. Controllala e premi «Invia la newsletter».
      </p>

      {items.length > 0 && (
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Oggetto</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="newsletter-subject-input" className="rounded-sm mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Introduzione</label>
            <Textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={2} data-testid="newsletter-intro-input" className="rounded-sm mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Discorsi inclusi ({nSelected})</label>
            <div className="mt-2 space-y-2">
              {items.map((it) => (
                <label key={it.id} className="flex items-start gap-3 p-3 border border-border rounded-sm cursor-pointer" data-testid={`newsletter-item-${it.id}`}>
                  <input
                    type="checkbox"
                    checked={!!selected[it.id]}
                    onChange={(e) => setSelected((s) => ({ ...s, [it.id]: e.target.checked }))}
                    className="mt-1 h-4 w-4 accent-primary"
                  />
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{it.leader} · {it.country}</div>
                    <div className="text-sm font-medium truncate">{it.title}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end gap-2 p-3 border border-dashed border-border rounded-sm bg-muted/30">
            <div className="flex-1">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Invio di prova</label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="tua@email.it"
                data-testid="newsletter-test-email-input"
                className="rounded-sm mt-1"
              />
            </div>
            <Button
              onClick={sendTest}
              disabled={testing || nSelected === 0 || !subject.trim() || !testEmail.trim()}
              variant="outline"
              className="rounded-sm gap-1.5"
              data-testid="newsletter-test-send-btn"
            >
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Invia prova a questo indirizzo
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={preview} disabled={previewing || nSelected === 0} variant="outline" className="rounded-sm gap-1.5" data-testid="newsletter-preview-btn">
              {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
              Anteprima
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button disabled={sending || !subject.trim() || nSelected === 0} className="rounded-sm gap-1.5" data-testid="newsletter-send-btn">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Invia la newsletter
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Inviare la newsletter?</AlertDialogTitle>
                  <AlertDialogDescription>
                    L'email verrà inviata subito a tutti gli iscritti confermati
                    ({status?.confirmed ?? 0}). L'operazione non è annullabile.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                  <AlertDialogAction onClick={send} data-testid="newsletter-send-confirm">Invia ora</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle>Anteprima email — {subject || "(senza oggetto)"}</DialogTitle>
                <DialogDescription>Così apparirà il messaggio agli iscritti (con nome di esempio).</DialogDescription>
              </DialogHeader>
              <iframe
                title="Anteprima newsletter"
                srcDoc={previewHtml}
                data-testid="newsletter-preview-iframe"
                className="w-full flex-1 min-h-[420px] rounded-sm border border-border bg-white"
              />
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
};
